<script setup lang="ts">
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useI18n } from "vue-i18n";
import { MONTHS_KEYS } from "@/constants/chineseCalendar";
import AppSelect from "@/components/inputs/AppSelect.vue";
import {
  computed,
  ref,
  defineProps,
  defineEmits,
  onBeforeMount,
  defineModel,
} from "vue";

const props = defineProps<{
  isLoading: boolean;
  initDate: Date | undefined;
}>();

const emits = defineEmits<{
  (e: "calculateMatrix", day: number, month: number, year: number): void;
}>();
const userName = defineModel<string | null>({
  default: null,
});

const { t } = useI18n();

const selectedDay = ref();
const selectedMonth = ref();
const selectedYear = ref();

const supportedYears = ref();
const monthDays = computed(() => {
  if (selectedMonth.value === undefined || selectedYear.value === undefined) {
    return [];
  }
  const daysInMonth = new Date(
    selectedYear.value,
    selectedMonth.value,
    0
  ).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
});

function generateDropdownYears() {
  const startYear = selectedYear.value - 100;
  supportedYears.value = Array.from({ length: 200 }, (_, i) => startYear + i);
}

function selectCurrentDay() {
  const today = props.initDate ?? new Date();
  selectedMonth.value = today.getMonth();
  selectedYear.value = today.getFullYear();
  selectedDay.value = today.getDate();
}

onBeforeMount(() => {
  selectCurrentDay();
  generateDropdownYears();
});

function handleSubmit() {
  emits(
    "calculateMatrix",
    selectedDay.value,
    selectedMonth.value + 1,
    selectedYear.value
  );
}
</script>

<template>
  <div class="bg-white rounded-3xl p-3 xl:px-6">
    <div class="flex flex-col gap-3 items-center">
      <p class="font-bold text-lg">
        {{ t("calculators.destiny-matrix.input-card.title") }}
      </p>

      <div class="flex flex-col gap-2">
        <!-- SELECTORS -->
        <div class="flex gap-2">
          <!-- DAY SELECT -->
          <div>
            <p class="text-brown text-sm">
              {{ t("calculators.destiny-matrix.input-card.day") }}
            </p>
            <AppSelect
              class="w-24"
              :items="monthDays"
              :default-value="selectedDay"
              @select-option="(newSelectedDay: number) => selectedDay = newSelectedDay"
            />
          </div>

          <!-- MONTH SELECT -->
          <div>
            <p class="text-brown text-sm">
              {{ t("calculators.destiny-matrix.input-card.month") }}
            </p>
            <AppSelect
              class="w-32"
              :items="MONTHS_KEYS"
              :default-value="MONTHS_KEYS[selectedMonth]"
              @select-option="(newSelectedMonth: string) => selectedMonth = MONTHS_KEYS.indexOf(newSelectedMonth)"
            >
              <template #option="{ item }">
                {{ t(`months.${item}`) }}
              </template>
            </AppSelect>
          </div>

          <!-- YEAR SELECT -->
          <div>
            <p class="text-brown text-sm">
              {{ t("calculators.destiny-matrix.input-card.year") }}
            </p>
            <AppSelect
              class="w-24"
              :items="supportedYears"
              :default-value="selectedYear"
              @select-option="(newSelectedYear: number) => selectedYear = newSelectedYear"
            />
          </div>
        </div>

        <input
          class="w-full h-[52px] rounded-[14px] border border-muted px-4 text-brown focus:outline-none sf-pro"
          type="text"
          :placeholder="t('calculators.destiny-matrix.input-card.name_input')"
          v-model="userName"
        />
      </div>

      <GradientButton
        class="w-[153px] h-[46px] md:w-[191px] md:h-14"
        bg-color="bg-brown-gradient"
        hover-bg-color="bg-orange-gradient"
        :text="t('buttons.calculate')"
        @click="handleSubmit"
        :is-loading="isLoading"
      />
    </div>
  </div>
</template>
